import { useMemo, useState } from "react";

import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";

import { formatDate, formatNumber } from "@/features/shared";
import { CustomDialogTitle, CustomInputField, CustomSelect } from "@/features/ui";

import { useInventoryValueMovementDetail } from "../hooks/use-report-document-request";
import { ReportDocumentFrame } from "../report-frame";
import {
  buildMovementBody,
  MOVEMENT_BLOCK_OPTIONS,
} from "../utils/report-request";

const PAGE_SIZE_OPTIONS = [100, 250, 500].map((size) => ({
  value: size,
  label: String(size),
}));

/**
 * Explicit drill-down beside the report: authorized warehouse, exact SKU and balance
 * block. Every request is re-authorized and recalculated by the backend.
 */
export const InventoryValueReportMovementDialog = ({
  open,
  onClose,
  outletId,
  userId,
  appliedBody,
  warehouses,
  maxSkuLength,
  onRefreshSummary,
}) => {
  const selectableWarehouses = useMemo(
    () =>
      appliedBody.warehouseIds
        ? warehouses.filter(({ id }) => appliedBody.warehouseIds.includes(id))
        : warehouses,
    [appliedBody, warehouses]
  );

  const [warehouseId, setWarehouseId] = useState(
    selectableWarehouses[0]?.id ?? ""
  );
  const [sku, setSku] = useState("");
  const [block, setBlock] = useState("closing");
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0].value);
  const [skuError, setSkuError] = useState(null);

  const detail = useInventoryValueMovementDetail({ outletId, userId });
  const shown = detail.document;
  const metadata = shown?.metadata;
  const selection = shown?.context;
  const totalPages = metadata
    ? Math.max(1, Math.ceil(metadata.rowCount / metadata.pageSize))
    : 1;

  const loadPage = (nextSelection, page) =>
    detail.load({
      outletId,
      body: buildMovementBody({
        appliedBody,
        warehouseId: nextSelection.warehouseId,
        sku: nextSelection.sku,
        block: nextSelection.block,
        page,
        pageSize: nextSelection.pageSize,
      }),
      context: nextSelection,
    });

  const handleShow = () => {
    if (!sku) {
      setSkuError("Masukkan kode barang persis seperti pada laporan.");
      return;
    }
    if (sku.length > maxSkuLength) {
      setSkuError(`Maksimal ${maxSkuLength} karakter.`);
      return;
    }
    setSkuError(null);
    loadPage({ warehouseId, sku, block, pageSize }, 1);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <CustomDialogTitle title="Rincian Pergerakan" onClose={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={onRefreshSummary}>
                Muat ulang ringkasan
              </Button>
            }
          >
            Rincian dihitung ulang saat dibuka; hasil dapat berbeda dari
            pratinjau sebelumnya. Periode mengikuti laporan yang diterapkan (
            {formatDate(appliedBody.startDate)} - {formatDate(appliedBody.endDate)}).
          </Alert>
          <Grid container spacing={2} sx={{ alignItems: "flex-end" }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <CustomSelect
                label="Gudang"
                options={selectableWarehouses.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
                disabled={selectableWarehouses.length === 0}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomInputField
                label="Kode Barang"
                placeholder="Sama persis dengan kode di laporan"
                value={sku}
                onChange={(event) => setSku(event.target.value)}
                error={Boolean(skuError)}
                helperText={skuError}
                inputProps={{ maxLength: maxSkuLength }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <CustomSelect
                label="Kolom saldo"
                options={MOVEMENT_BLOCK_OPTIONS}
                value={block}
                onChange={(event) => setBlock(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 1 }}>
              <CustomSelect
                label="Per halaman"
                options={PAGE_SIZE_OPTIONS}
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleShow}
                disabled={detail.loading || !warehouseId}
                sx={{ height: 40 }}
              >
                Tampilkan
              </Button>
            </Grid>
          </Grid>

          {detail.loading && <LinearProgress />}
          {detail.error && (
            <Alert severity="error">{detail.error.message}</Alert>
          )}

          {shown && (
            <>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "center", justifyContent: "flex-end" }}
              >
                <Typography variant="body2" sx={{ mr: "auto" }}>
                  Halaman {metadata.page} dari {totalPages} ·{" "}
                  {formatNumber(metadata.rowCount)} pergerakan · ID {metadata.reportId}
                </Typography>
                <Button
                  size="small"
                  onClick={() => loadPage(selection, metadata.page - 1)}
                  disabled={detail.loading || metadata.page <= 1}
                >
                  Sebelumnya
                </Button>
                <Button
                  size="small"
                  onClick={() => loadPage(selection, metadata.page + 1)}
                  disabled={detail.loading || metadata.page >= totalPages}
                >
                  Berikutnya
                </Button>
              </Stack>
              <ReportDocumentFrame
                key={metadata.reportId}
                html={shown.html}
                title="Rincian pergerakan Nilai Persediaan"
                sx={{ height: "60vh" }}
              />
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

InventoryValueReportMovementDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  outletId: PropTypes.string.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  appliedBody: PropTypes.shape({
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    warehouseIds: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  warehouses: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })
  ).isRequired,
  maxSkuLength: PropTypes.number.isRequired,
  onRefreshSummary: PropTypes.func.isRequired,
};
